/**
 * PostOptionsDropdown - Menu options for posts
 * Web version adapted from mobile app
 */

import {
  Trash2,
  EyeOff,
  VolumeX,
  Ban,
  Copy,
  Flag
} from 'lucide-react'
import { useState, useRef, useEffect } from 'react'

interface PostOptionsDropdownProps {
  isOpen: boolean
  onClose: () => void
  isOwner: boolean
  post: {
    id: string
    authorId?: string
    content?: string
    author: {
      name: string
      username: string
    }
  }
  buttonPosition?: { x: number; y: number }
  showDelete?: boolean // Control whether to show delete option
}

export default function PostOptionsDropdown({
  isOpen,
  onClose,
  isOwner,
  post,
  buttonPosition,
  showDelete = false,
}: PostOptionsDropdownProps) {
  const menuRef = useRef<HTMLDivElement>(null)

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return

    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen, onClose])

  if (!isOpen) return null

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this post?')) {
      onClose()
      return
    }

    try {
      const response = await fetch(`/api/posts/${post.id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      })

      if (response.ok) {
        alert('Post deleted successfully')
        window.location.reload() // Refresh to update feed
      } else {
        alert('Failed to delete post')
      }
    } catch (error) {
      console.error('Delete post error:', error)
      alert('Failed to delete post')
    }
    onClose()
  }

  const handleNotInterested = async () => {
    try {
      const notInterestedPosts = JSON.parse(
        localStorage.getItem('not_interested_posts') || '[]'
      )

      if (!notInterestedPosts.includes(post.id)) {
        notInterestedPosts.push(post.id)
        localStorage.setItem('not_interested_posts', JSON.stringify(notInterestedPosts))
      }

      alert("Thanks for your feedback. We'll show you less content like this.")
    } catch (error) {
      console.error('Not interested error:', error)
      alert('Failed to update preferences')
    }
    onClose()
  }

  const handleMuteUser = async () => {
    if (!window.confirm(
      `Are you sure you want to mute @${post.author.username}? You won't see their posts in your feed.`
    )) {
      onClose()
      return
    }

    try {
      const mutedUsers = JSON.parse(localStorage.getItem('muted_users') || '[]')

      if (!mutedUsers.includes(post.authorId)) {
        mutedUsers.push(post.authorId)
        localStorage.setItem('muted_users', JSON.stringify(mutedUsers))
      }

      alert(`@${post.author.username} has been muted`)
    } catch (error) {
      console.error('Mute user error:', error)
      alert('Failed to mute user')
    }
    onClose()
  }

  const handleBlockUser = async () => {
    if (!window.confirm(
      `Are you sure you want to block @${post.author.username}? They won't be able to see your profile or content, and you won't see theirs.`
    )) {
      onClose()
      return
    }

    try {
      const blockedUsers = JSON.parse(localStorage.getItem('blocked_users') || '[]')

      if (!blockedUsers.includes(post.authorId)) {
        blockedUsers.push(post.authorId)
        localStorage.setItem('blocked_users', JSON.stringify(blockedUsers))
      }

      alert(`@${post.author.username} has been blocked`)
    } catch (error) {
      console.error('Block user error:', error)
      alert('Failed to block user')
    }
    onClose()
  }

  const handleCopyText = async () => {
    try {
      if (!post.content || post.content.trim().length === 0) {
        alert('This post has no text to copy')
        onClose()
        return
      }

      await navigator.clipboard.writeText(post.content.trim())
      alert('Text copied to clipboard')
    } catch (error) {
      console.error('Copy text error:', error)
      alert('Failed to copy text')
    }
    onClose()
  }

  const handleReport = async () => {
    const reason = window.prompt(
      'Why are you reporting this post?\n\n' +
      '1. Spam\n' +
      '2. Inappropriate Content\n' +
      '3. Harassment\n' +
      '4. False Information\n\n' +
      'Enter number (1-4):'
    )

    if (!reason) {
      onClose()
      return
    }

    const reasons: Record<string, string> = {
      '1': 'spam',
      '2': 'inappropriate',
      '3': 'harassment',
      '4': 'misinformation',
    }

    const selectedReason = reasons[reason] || 'other'

    try {
      const reports = JSON.parse(localStorage.getItem('user_reports') || '[]')

      const report = {
        postId: post.id,
        authorId: post.authorId,
        reason: selectedReason,
        timestamp: new Date().toISOString(),
        reportId: `report_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      }

      reports.push(report)
      localStorage.setItem('user_reports', JSON.stringify(reports))

      alert("Thanks for your report. We'll review this content and take appropriate action if needed.")
    } catch (error) {
      console.error('Submit report error:', error)
      alert('Failed to submit report')
    }
    onClose()
  }

  // Calculate position - appear directly below the button
  const menuStyle: React.CSSProperties = buttonPosition
    ? {
        position: 'fixed',
        top: buttonPosition.y + 8,
        left: buttonPosition.x - 200, // Position to the left of button
        zIndex: 1000,
      }
    : {
        position: 'absolute',
        top: '100%',
        right: 0,
        marginTop: '0.5rem',
        zIndex: 1000,
      }

  return (
    <>
      {/* Dropdown Menu */}
      <div
        ref={menuRef}
        style={menuStyle}
        className="bg-white rounded-xl shadow-lg min-w-[200px] max-w-[280px] overflow-hidden z-50"
      >
        {/* Show delete option first if owner and showDelete is true */}
        {isOwner && showDelete && (
          <button
            onClick={handleDelete}
            className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors text-left border-b border-gray-100"
          >
            <Trash2 className="w-5 h-5 text-red-500" />
            <span className="text-red-500 font-medium">Delete Post</span>
          </button>
        )}

        {/* Always show these options for all users */}
        <>
            <button
              onClick={handleNotInterested}
              className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors text-left border-b border-gray-100"
            >
              <EyeOff className="w-5 h-5 text-gray-700" />
              <span className="text-gray-900">Not interested</span>
            </button>

            <button
              onClick={handleMuteUser}
              className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors text-left border-b border-gray-100"
            >
              <VolumeX className="w-5 h-5 text-gray-700" />
              <span className="text-gray-900">Mute user</span>
            </button>

            <button
              onClick={handleBlockUser}
              className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors text-left border-b border-gray-100"
            >
              <Ban className="w-5 h-5 text-red-500" />
              <span className="text-red-500 font-medium">Block user</span>
            </button>

            <button
              onClick={handleCopyText}
              className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors text-left border-b border-gray-100"
            >
              <Copy className="w-5 h-5 text-gray-700" />
              <span className="text-gray-900">Copy text</span>
            </button>

            <button
              onClick={handleReport}
              className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors text-left"
            >
              <Flag className="w-5 h-5 text-red-500" />
              <span className="text-red-500 font-medium">Report post</span>
            </button>
        </>
      </div>
    </>
  )
}
